import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import {
  formatStockPrice,
  formatPriceChange,
  formatDisplayCurrencyValue,
  getDisplayCurrencySymbol,
} from '../utils/stockPriceFormatter';

/**
 * Context value for stock currency information
 */
interface StockCurrencyContextValue {
  /** The currency code (e.g., 'USD', 'GBX', 'EUR') */
  currency: string | undefined;
  /** The exchange name (e.g., 'NASDAQ', 'LSE') */
  exchange: string | undefined;
  /** The display currency symbol (£ for GBX, $ for USD, etc.) */
  currencySymbol: string;
  /**
   * Format a raw API price using the current stock's currency.
   * For UK stocks (GBX), this converts pence to pounds.
   * Use this for quote.price, quote.change etc.
   * @param price - The price value to format (in pence for UK stocks)
   * @param decimals - Number of decimal places (default: 2)
   */
  formatPrice: (price: number | null | undefined, decimals?: number) => string;
  /**
   * Format a price change (e.g., +$1.50 or -£0.05)
   * @param change - The change value to format
   * @param decimals - Number of decimal places (default: 2)
   */
  formatChange: (change: number | null | undefined, decimals?: number) => string;
  /**
   * Format a value that's already in display currency (not raw pence).
   * Use this for calculated projections, financial statement data (EPS, revenue, etc.)
   * which are already in pounds for UK stocks.
   * @param value - The value to format (already in display currency)
   * @param decimals - Number of decimal places (default: 2)
   */
  formatDisplayPrice: (value: number | null | undefined, decimals?: number) => string;
}

/**
 * Default context value when no provider is present
 * Falls back to USD formatting
 */
const defaultValue: StockCurrencyContextValue = {
  currency: 'USD',
  exchange: undefined,
  currencySymbol: '$',
  formatPrice: (price, decimals = 2) =>
    formatStockPrice({ price, currency: 'USD', decimals }),
  formatChange: (change, decimals = 2) =>
    formatPriceChange({ price: change, currency: 'USD', decimals }),
  formatDisplayPrice: (value, decimals = 2) =>
    formatDisplayCurrencyValue({ price: value, currency: 'USD', decimals }),
};

const StockCurrencyContext = createContext<StockCurrencyContextValue>(defaultValue);

/**
 * Props for the StockCurrencyProvider component
 */
interface StockCurrencyProviderProps {
  /** The currency code from the company profile (e.g., 'USD', 'GBX', 'EUR') */
  currency?: string;
  /** The exchange name from the company profile (e.g., 'NASDAQ', 'LSE') */
  exchange?: string;
  /** Child components that will have access to the currency context */
  children: ReactNode;
}

/**
 * Provider component that makes stock currency information available to all child components.
 *
 * Wrap your single-stock page content with this provider to enable currency-aware
 * price formatting throughout the component tree.
 *
 * @example
 * // In ViewTicker.tsx or ProjectionsWrapper.tsx
 * const { data: profileData } = useCompanyProfile(ticker);
 * const profile = Array.isArray(profileData) ? profileData[0] : profileData;
 *
 * return (
 *   <StockCurrencyProvider currency={profile?.currency} exchange={profile?.exchange}>
 *     <CompactHeader ... />
 *     <Charts ... />
 *   </StockCurrencyProvider>
 * );
 *
 * // In child components
 * const { formatPrice, formatChange, currency } = useStockCurrency();
 * return <Typography>{formatPrice(stock.price)}</Typography>;
 */
export const StockCurrencyProvider: React.FC<StockCurrencyProviderProps> = ({
  currency,
  exchange,
  children,
}) => {
  const value = useMemo<StockCurrencyContextValue>(
    () => ({
      currency,
      exchange,
      currencySymbol: getDisplayCurrencySymbol(currency),
      formatPrice: (price, decimals = 2) =>
        formatStockPrice({ price, currency, decimals }),
      formatChange: (change, decimals = 2) =>
        formatPriceChange({ price: change, currency, decimals }),
      formatDisplayPrice: (value, decimals = 2) =>
        formatDisplayCurrencyValue({ price: value, currency, decimals }),
    }),
    [currency, exchange]
  );

  return (
    <StockCurrencyContext.Provider value={value}>
      {children}
    </StockCurrencyContext.Provider>
  );
};

/**
 * Hook to access the stock currency context.
 *
 * Use this in any component nested within a StockCurrencyProvider to format
 * prices with the correct currency symbol and conversion (e.g., pence to pounds).
 *
 * @example
 * const { formatPrice, formatChange, currency } = useStockCurrency();
 *
 * // Format a stock price
 * <Typography>{formatPrice(stock.price)}</Typography>
 *
 * // Format a price change
 * <Typography>{formatChange(stock.change)}</Typography>
 *
 * // Check the currency if needed
 * {currency === 'GBX' && <Chip label="UK Stock" />}
 *
 * @returns StockCurrencyContextValue with formatPrice, formatChange, currency, and exchange
 */
export const useStockCurrency = (): StockCurrencyContextValue => {
  return useContext(StockCurrencyContext);
};

export default StockCurrencyContext;
